/*
 * Guardar en Drive desde el portal y decir cuándo llegó (O7/O8/O9, auditoría 2026-10-09).
 *
 * Ctrl+S con el foco FUERA del documento (cabecera, Aurora, compartir…) lo recibía el portal y no hacía
 * nada, y la cabecera nunca decía «Guardado». Ahora:
 *   · Ctrl+S fuera del documento pulsa el MISMO botón Guardar del motor: es exactamente un Ctrl+S dentro
 *     (el motor junta lo pendiente y lo manda a Drive). Comprobado contra el motor 9.4.
 *   · Tras cualquier petición de guardado (Ctrl+S dentro o fuera, botón Guardar) se pregunta a Drive
 *     (`GET /office/guardado/{id}`) y solo se dice «Guardado en Drive · hora» cuando Drive cambió de verdad.
 *     Los avisos de inicio y fin de guardado del motor NO sirven para eso: salen también en cada
 *     autoguardado de la coedición, mientras se escribe. Solo se usan para saber si queda algo en curso.
 *   · Si el motor avisa de que Drive no recibió el guardado (−331) o no contestó (−101), se dice y se
 *     puede reintentar.
 *
 * Bugs 5 y 7 (2026-10-09, «KPIS orbis 2.0»): TODO aviso del motor salía como «No se pudo guardar en Drive» con
 * «Reintentar» y la cabecera en rojo, también la referencia circular (−320) que el motor manda al abrir el libro y
 * el de rellenar/ordenar con celdas combinadas (−81), aunque el archivo se guardaba bien. Ahora solo los códigos de
 * FALLOS_DE_GUARDADO son un fallo (con su motivo); la referencia circular es un aviso informativo, una vez por
 * sesión y con la celda; los demás avisos ya los enseña el motor en su ventana y el guardado sigue su curso.
 * Esa ventana «Aviso» del motor no se puede apagar por configuración (Main.js 9.4 la abre para todo error no
 * crítico); hacerlo exigiría parchear el motor y no se hace.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { clickInEditor } from './skin';
import { motorDe } from './editorComandos';

export const BOTON_GUARDAR = ['#slot-btn-save button', '#id-toolbar-btn-save'];
const SONDEO_MS = 1000;
const SIN_ACTIVIDAD_MS = 2000; // sin guardado en curso ni empezado en este rato: no había nada que guardar
const MAXIMO_MS = 90000;

const esCtrlS = (e) => (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key || '').toLowerCase() === 's';

/** Pide al motor que guarde. Devuelve false si el editor todavía no tiene con qué. */
export function pulsarGuardar(iframe) {
  if (clickInEditor(iframe, BOTON_GUARDAR, { hidden: true })) return true;
  const motor = motorDe(iframe);
  if (typeof motor?.asc_Save === 'function') {
    motor.asc_Save(false);
    return true;
  }
  return false;
}

function idsDeGuardado(iframe) {
  try {
    const A = iframe?.contentWindow?.Asc?.c_oAscAsyncAction || {};
    return [A.Save, A.ForceSaveButton, A.ForceSaveTimeout].filter((id) => typeof id === 'number');
  } catch {
    return [];
  }
}

/** Avisa cuando el motor empieza y termina un guardado (de Drive o de la coedición). Devuelve con qué dejar de escuchar. */
export function escucharAccionesDeGuardado(iframe, { alEmpezar, alTerminar }) {
  let parado = false;
  let reloj = null;
  let intentos = 0;
  let motor = null;
  const esGuardado = (id) => idsDeGuardado(iframe).includes(id);
  const inicio = (_tipo, id) => { if (!parado && esGuardado(id)) alEmpezar(); };
  const fin = (_tipo, id) => { if (!parado && esGuardado(id)) alTerminar(); };
  const enganchar = () => {
    if (parado) return;
    motor = motorDe(iframe);
    if (typeof motor?.asc_registerCallback !== 'function') {
      intentos += 1;
      if (intentos < 40) reloj = setTimeout(enganchar, 250);
      return;
    }
    motor.asc_registerCallback('asc_onStartAction', inicio);
    motor.asc_registerCallback('asc_onEndAction', fin);
  };
  enganchar();
  return () => {
    parado = true;
    clearTimeout(reloj);
    try {
      motor?.asc_unregisterCallback?.('asc_onStartAction', inicio);
      motor?.asc_unregisterCallback?.('asc_onEndAction', fin);
    } catch {
      /* el iframe ya no existe */
    }
  };
}

/** Ctrl+S y el botón Guardar DENTRO del motor (el motor guarda solo; aquí solo se entera el portal). */
export function escucharPedidosDeGuardado(iframe, alPedir) {
  let win = null;
  let doc = null;
  const tecla = (e) => { if (esCtrlS(e)) alPedir(); };
  const clic = (e) => { if (e.target?.closest?.(BOTON_GUARDAR.join(','))) alPedir(); };
  try {
    win = iframe.contentWindow;
    doc = iframe.contentDocument;
    win.addEventListener('keydown', tecla, true);
    doc.addEventListener('click', clic, true);
  } catch {
    return () => {};
  }
  return () => {
    try {
      win.removeEventListener('keydown', tecla, true);
      doc.removeEventListener('click', clic, true);
    } catch {
      /* el iframe ya no existe */
    }
  };
}

// Avisos del motor que SÍ son un guardado que no llegó a Drive (Asc.c_oAscError.ID del motor 9.4) y su motivo
export const FALLOS_DE_GUARDADO = {
  [-331]: 'Drive no recibió el último guardado. Tus cambios siguen en el editor.', // ForceSaveButton
  [-332]: 'Drive tardó demasiado en recibir el guardado. Tus cambios siguen en el editor.', // ForceSaveTimeout
  [-101]: 'Drive no respondió al editor. Tus cambios siguen en el editor.', // Warning (errorConnectToServer)
  [-25]: 'El editor tuvo un error y dejó de guardar. Descarga una copia para no perder tus cambios.', // EditingError
  [-18]: 'Se perdió la conexión con el editor: lo que escribas ahora no se guarda.', // CoAuthoringDisconnect
};
export const REFERENCIA_CIRCULAR = -320;

export const esFalloDeGuardado = (aviso) => Object.prototype.hasOwnProperty.call(FALLOS_DE_GUARDADO, aviso?.warningCode);

/** Texto de un aviso del motor, sin el HTML que trae. */
export function textoDeAviso(aviso) {
  if (esFalloDeGuardado(aviso)) return FALLOS_DE_GUARDADO[aviso.warningCode];
  return String(aviso?.warningDescription || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    || 'El editor avisó de un problema al guardar.';
}

export const textoReferenciaCircular = (celda) => (celda
  ? `Referencia circular en ${celda}. El archivo se guardó.`
  : 'Hay una referencia circular en una fórmula. El archivo se guardó.');

function letrasDeColumna(col) {
  let s = '';
  for (let n = col + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** 'Hoja con espacios'!BW15 (fila y columna desde 0, como las guarda el motor). */
export function referenciaDeCelda(hoja, fila, col) {
  const simple = /^[A-Za-z_À-ɏ][\w.À-ɏ]*$/.test(hoja) && !/^[A-Za-z]{1,3}\d+$/.test(hoja);
  return `${simple ? hoja : `'${hoja.replace(/'/g, "''")}'`}!${letrasDeColumna(col)}${fila + 1}`;
}

/** Celda que el motor marcó con referencia circular: la de la hoja abierta o la de la hoja más cercana. */
export function celdaCircular(iframe) {
  try {
    const celdas = iframe?.contentWindow?.AscCommonExcel?.g_cCalcRecursion?.getCycleCells?.() || [];
    if (!celdas.length) return null;
    const activa = motorDe(iframe)?.wbModel?.getActive?.() ?? 0;
    const hoja = (c) => c.ws?.getIndex?.() ?? 0;
    const [c] = [...celdas].sort((a, b) => (Math.abs(hoja(a) - activa) - Math.abs(hoja(b) - activa))
      || (hoja(a) - hoja(b)) || (a.nRow - b.nRow) || (a.nCol - b.nCol));
    return referenciaDeCelda(c.ws.getName(), c.nRow, c.nCol);
  } catch {
    return null;
  }
}

/**
 * Anota la celda de cada referencia circular que detecta el motor. Su aviso (onWarning) no dice dónde y llega
 * cuando el motor ya vació su lista de celdas en ciclo: por eso se lee dentro de su propio `asc_onError`, que
 * corre en el mismo recálculo. Se engancha en cuanto existe el motor, antes de que abra el libro.
 */
export function vigilarReferenciaCircular(iframe, alVer, { intervaloMs = 100, maximoMs = 180000 } = {}) {
  let motor = null;
  let reloj = null;
  const t0 = Date.now();
  const alError = (id) => {
    if (id === REFERENCIA_CIRCULAR) alVer(celdaCircular(iframe));
  };
  const enganchar = () => {
    const m = motorDe(iframe);
    if (typeof m?.asc_registerCallback === 'function') {
      motor = m;
      motor.asc_registerCallback('asc_onError', alError);
      return;
    }
    if (Date.now() - t0 < maximoMs) reloj = setTimeout(enganchar, intervaloMs);
  };
  enganchar();
  return () => {
    clearTimeout(reloj);
    try {
      motor?.asc_unregisterCallback?.('asc_onError', alError);
    } catch {
      /* el iframe ya no existe */
    }
  };
}

/**
 * Estado del guardado en Drive para la cabecera.
 * `estado`: null (nada pedido) · 'guardando' · 'guardado' (`hora` = ISO de Drive) · 'al_dia' (no había
 * nada pendiente) · 'lento' · 'error'.
 */
export function useGuardadoEnDrive({ fileId, linkToken, iframeRef, ready, canEdit }) {
  const [estado, setEstado] = useState({ estado: null, hora: null });
  const antes = useRef(null); // updated_at de Drive conocido
  const sondeo = useRef(null);
  const enCurso = useRef(0);
  const ultimaActividad = useRef(0);
  const vivo = useRef(true);
  const permiso = useRef({ ready, canEdit });
  permiso.current = { ready, canEdit };

  useEffect(() => () => {
    vivo.current = false;
    clearTimeout(sondeo.current);
  }, []);

  const consultar = useCallback(async (timeout = 15000) => {
    const params = linkToken ? { link: linkToken } : undefined;
    const { data } = await api.get(`/office/guardado/${fileId}`, { params, timeout });
    return data || {};
  }, [fileId, linkToken]);
  const leer = useCallback(async () => (await consultar()).updated_at || null, [consultar]);

  const vigilar = useCallback(() => {
    clearTimeout(sondeo.current);
    const t0 = Date.now();
    setEstado({ estado: 'guardando', hora: null });
    const paso = async () => {
      if (!vivo.current) return;
      let actual = null;
      try {
        actual = await leer();
      } catch {
        /* sin red un momento: se vuelve a preguntar */
      }
      if (!vivo.current) return;
      if (actual && antes.current && actual !== antes.current) {
        antes.current = actual;
        setEstado({ estado: 'guardado', hora: actual });
        return;
      }
      if (actual && !antes.current) antes.current = actual;
      const ahora = Date.now();
      if (ahora - t0 > SIN_ACTIVIDAD_MS && enCurso.current === 0 && ahora - ultimaActividad.current > SIN_ACTIVIDAD_MS) {
        setEstado({ estado: 'al_dia', hora: antes.current });
        return;
      }
      if (ahora - t0 > MAXIMO_MS) {
        setEstado({ estado: 'lento', hora: null });
        return;
      }
      sondeo.current = setTimeout(paso, SONDEO_MS);
    };
    sondeo.current = setTimeout(paso, 800);
  }, [leer]);

  const guardarEnDrive = useCallback(() => {
    const { ready: listo, canEdit: puede } = permiso.current;
    if (!listo) {
      toast.message('Espera a que termine de abrir el documento');
      return false;
    }
    if (!puede) {
      toast.message('Este archivo es de solo lectura: no hay cambios que guardar');
      return false;
    }
    if (!pulsarGuardar(iframeRef.current)) {
      toast.error('El editor no respondió. Intenta guardar de nuevo en un momento.');
      return false;
    }
    vigilar();
    return true;
  }, [iframeRef, vigilar]);

  // Ctrl+S con el foco en el portal (fuera del iframe): antes se lo tragaba DriveEditor sin hacer nada
  const guardarRef = useRef(guardarEnDrive);
  guardarRef.current = guardarEnDrive;
  useEffect(() => {
    const onKey = (e) => {
      if (!esCtrlS(e)) return;
      e.preventDefault();
      guardarRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Con el editor listo: lo que hace el propio motor (Ctrl+S dentro, botón Guardar, autoguardados)
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!ready || !iframe) return undefined;
    leer().then((v) => { if (vivo.current && v && !antes.current) antes.current = v; }).catch(() => {});
    const quitarAcciones = escucharAccionesDeGuardado(iframe, {
      alEmpezar: () => { enCurso.current += 1; ultimaActividad.current = Date.now(); },
      alTerminar: () => { enCurso.current = Math.max(0, enCurso.current - 1); ultimaActividad.current = Date.now(); },
    });
    const quitarPedidos = escucharPedidosDeGuardado(iframe, () => { if (permiso.current.canEdit) vigilar(); });
    return () => {
      quitarAcciones();
      quitarPedidos();
      enCurso.current = 0;
    };
  }, [ready, iframeRef, leer, vigilar]);

  const circularAvisada = useRef(false);
  const alAviso = useCallback(async (aviso) => {
    if (!esFalloDeGuardado(aviso)) {
      // Aviso del contenido (referencia circular, celdas combinadas…): el archivo se guarda igual. Ni error, ni
      // «Reintentar», ni cabecera en rojo, y el guardado que esté en curso se sigue vigilando.
      if (aviso?.warningCode === REFERENCIA_CIRCULAR && !circularAvisada.current) {
        circularAvisada.current = true;
        toast.info(textoReferenciaCircular(aviso.celda), { duration: 10000 });
      }
      return;
    }
    clearTimeout(sondeo.current);
    setEstado({ estado: 'error', hora: null });
    // Si fue Drive quien rechazó el guardado (p. ej. un Excel con imágenes en celda), su motivo manda
    let motivo = textoDeAviso(aviso);
    try {
      motivo = (await consultar(5000)).rechazo?.motivo || motivo;
    } catch {
      /* sin red: el motivo del motor */
    }
    if (!vivo.current) return;
    toast.error('No se pudo guardar en Drive', {
      description: motivo,
      duration: 15000,
      action: { label: 'Reintentar', onClick: () => guardarRef.current() },
    });
  }, [consultar]);

  return { ...estado, guardarEnDrive, alAviso };
}
